import React from 'react';
import { Layout } from '../components/layout/Layout';
import { Eye, Keyboard, Monitor, Volume2, MousePointer, MessageSquare } from 'lucide-react';

export const AccessibilityPage = () => {
  return (
    <Layout>
      <div className="container-custom py-12 md:py-16 max-w-4xl" data-testid="accessibility-page">
        <h1 className="font-serif text-4xl md:text-5xl font-bold mb-8">Accessibility Statement</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-lg text-muted-foreground mb-8">
            The Trade Media is committed to ensuring digital accessibility for people with disabilities. 
            We are continually improving the user experience for everyone and applying the relevant 
            accessibility standards.
          </p>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">Our Commitment</h2>
            <p className="text-muted-foreground mb-4">
              We strive to ensure that our website is accessible to all users, regardless of ability or 
              technology. Our goal is to meet or exceed the Web Content Accessibility Guidelines (WCAG) 2.1 
              Level AA standards.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-6">Accessibility Features</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 border border-border rounded-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-primary/10 rounded-sm">
                    <Keyboard className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-medium">Keyboard Navigation</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Our website can be navigated using only a keyboard. Use Tab to move between 
                  interactive elements and Enter to activate them.
                </p>
              </div>

              <div className="p-6 border border-border rounded-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-primary/10 rounded-sm">
                    <Eye className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-medium">Screen Reader Support</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  We use semantic HTML and ARIA labels to ensure compatibility with screen readers 
                  and other assistive technologies.
                </p>
              </div>

              <div className="p-6 border border-border rounded-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-primary/10 rounded-sm">
                    <Monitor className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-medium">Responsive Design</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Our website adapts to different screen sizes and devices, supporting zoom up to 
                  200% without loss of content or functionality.
                </p>
              </div>

              <div className="p-6 border border-border rounded-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-primary/10 rounded-sm">
                    <Volume2 className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-medium">Media Alternatives</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Images include descriptive alt text, and we strive to provide captions or 
                  transcripts for video content where available.
                </p>
              </div>

              <div className="p-6 border border-border rounded-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-primary/10 rounded-sm">
                    <MousePointer className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-medium">Clear Focus Indicators</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Interactive elements have visible focus indicators to help users understand 
                  their current position on the page.
                </p>
              </div>

              <div className="p-6 border border-border rounded-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-primary/10 rounded-sm">
                    <MessageSquare className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-medium">Plain Language</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  We write content in clear, plain language and organize information logically 
                  to make it easier to understand.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">Conformance Status</h2>
            <p className="text-muted-foreground mb-4">
              The Web Content Accessibility Guidelines (WCAG) defines requirements for designers and 
              developers to improve accessibility for people with disabilities. It defines three levels 
              of conformance: Level A, Level AA, and Level AAA.
            </p>
            <p className="text-muted-foreground">
              The Trade Media is partially conformant with WCAG 2.1 Level AA. Partially conformant means 
              that some parts of the content do not fully conform to the accessibility standard. We are 
              actively working to achieve full conformance.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">Browser and Assistive Technology Compatibility</h2>
            <p className="text-muted-foreground mb-4">
              Our website is designed to be compatible with the following:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Modern browsers including Chrome, Firefox, Safari, and Edge</li>
              <li>Screen readers including NVDA, JAWS, and VoiceOver</li>
              <li>Voice recognition software</li>
              <li>Screen magnification software</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">Known Limitations</h2>
            <p className="text-muted-foreground mb-4">
              Despite our best efforts, some content may not be fully accessible. Known limitations include:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Some older content may not have complete alt text descriptions</li>
              <li>Some third-party content or embedded media may not be fully accessible</li>
              <li>Some PDF documents may not be fully accessible to screen readers</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              We are working to address these issues and welcome your feedback.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">Feedback</h2>
            <p className="text-muted-foreground mb-4">
              We welcome your feedback on the accessibility of The Trade Media. Please let us know if 
              you encounter accessibility barriers:
            </p>
            <ul className="list-none text-muted-foreground space-y-2">
              <li><strong>Email:</strong> <a href="mailto:accessibility@thetrade.media" className="text-primary hover:underline">accessibility@thetrade.media</a></li>
              <li><strong>Phone:</strong> (123) 456-7890</li>
              <li><strong>Contact Form:</strong> <a href="/contact" className="text-primary hover:underline">Contact Us</a></li>
            </ul>
            <p className="text-muted-foreground mt-4">
              We try to respond to accessibility feedback within 5 business days.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">Technical Specifications</h2>
            <p className="text-muted-foreground">
              Accessibility of The Trade Media relies on the following technologies to work with the 
              particular combination of web browser and any assistive technologies or plugins installed 
              on your computer:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-4">
              <li>HTML</li>
              <li>WAI-ARIA</li>
              <li>CSS</li>
              <li>JavaScript</li>
            </ul>
          </section>

          <section className="p-6 bg-secondary/30 rounded-sm">
            <h2 className="font-serif text-xl font-bold mb-2">Assessment and Updates</h2>
            <p className="text-muted-foreground">
              This statement was last updated on January 22, 2026. We assess the accessibility of our 
              website regularly and update this statement as changes are made.
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default AccessibilityPage;
